import {
  AnswerOptionVariableDefTypeEnum,
  MasterFieldTypeEnum,
  ResponseTypeEnum,
  TemplateNodeSchema,
  TemplateQuestion,
  TemplateQuestionAnswerOption,
} from '@ulysses-inc/harami_api_client'
import { Modal, Select, Typography } from 'antd'
import { useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import {
  buildAnswerOptionQuestionPatch,
  selectProductMasterFieldDefsByFieldType,
} from 'src/state/ducks/templates/selectors'
import { RootState } from 'src/state/store'

const { Option } = Select
const { Text } = Typography

type Props = {
  onClose: () => void
  questionNode: TemplateNodeSchema
  onSave: (questionPatch: Partial<TemplateQuestion>) => void
}

type VariableDefOptionId = 'name' | 'code' | number

const convertToVariableDefOptionId = (
  answerOption?: TemplateQuestionAnswerOption,
): VariableDefOptionId | undefined => {
  if (!answerOption) {
    return undefined
  }
  switch (answerOption.variableDefType) {
    case AnswerOptionVariableDefTypeEnum.Item:
      return answerOption.itemVariableDef?.masterFieldDefId ?? 0
    case AnswerOptionVariableDefTypeEnum.Name:
      return 'name'
    case AnswerOptionVariableDefTypeEnum.Code:
      return 'code'
    default:
      return answerOption.variableDefType satisfies never
  }
}

const convertToAnswerOption = (variableDefOptionId: VariableDefOptionId) => {
  switch (variableDefOptionId) {
    case 'name':
      return {
        variableDefType: AnswerOptionVariableDefTypeEnum.Name,
      }
    case 'code':
      return {
        variableDefType: AnswerOptionVariableDefTypeEnum.Code,
      }
    default: {
      return {
        variableDefType: AnswerOptionVariableDefTypeEnum.Item,
        itemVariableDef: {
          // キャストしなくても動作するように見えるが、option の value は html 要素として展開されているようなので、
          // 念の為、 number にキャストしておく
          masterFieldDefId: Number(variableDefOptionId),
        },
      }
    }
  }
}

export const EditProductMasterAutofillSettingModal = ({
  onClose,
  questionNode,
  onSave,
}: Props) => {
  const productMasterFieldDefs = useSelector(
    (state: RootState) =>
      selectProductMasterFieldDefsByFieldType(
        state,
        questionNode.question?.responseType === ResponseTypeEnum.NUMBER
          ? MasterFieldTypeEnum.Number
          : MasterFieldTypeEnum.Text,
      ),
    shallowEqual,
  )
  const [selectedVariableDefOptionId, setSelectedVariableDefOptionId] =
    useState<VariableDefOptionId | undefined>(
      convertToVariableDefOptionId(questionNode.question?.answerOption),
    )

  const onOk = () => {
    if (!selectedVariableDefOptionId) {
      return
    }
    onSave(
      buildAnswerOptionQuestionPatch(
        convertToAnswerOption(selectedVariableDefOptionId),
      ),
    )
    onClose()
  }

  const onChange = (value: VariableDefOptionId) =>
    setSelectedVariableDefOptionId(value)

  return (
    <Modal
      title="製品マスタから自動入力"
      open={true}
      destroyOnClose={true}
      onOk={onOk}
      onCancel={() => onClose()}
      okText="保存"
      cancelText="キャンセル"
      okButtonProps={{
        disabled: selectedVariableDefOptionId === undefined,
      }}
      width={'480px'}
      centered
      styles={{
        body: {
          paddingTop: 12,
          paddingBottom: 20,
        },
        footer: {
          marginTop: 0, // ややこしくなるので、デフォルトのマージンはリセットする
        },
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <Text strong>自動入力させるマスタの項目</Text>
        <Select
          placeholder="項目を選択"
          onChange={onChange}
          value={selectedVariableDefOptionId}
          style={{
            width: '100%',
          }}
          aria-label="項目を選択"
        >
          {questionNode.question?.responseType === ResponseTypeEnum.TEXT && (
            <>
              <Option
                key={'name'}
                value={'name'}
                data-testid="select-master-field-def-autofill-item"
              >
                製品名
              </Option>
              <Option
                key={'code'}
                value={'code'}
                data-testid="select-master-field-def-autofill-item"
              >
                製品管理番号
              </Option>
            </>
          )}
          {productMasterFieldDefs.map(productMasterFieldDef => (
            <Option
              key={`item-${productMasterFieldDef.id}`}
              value={productMasterFieldDef.id}
              data-testid="select-master-field-def-autofill-item"
            >
              {productMasterFieldDef.name}
            </Option>
          ))}
        </Select>
      </div>
    </Modal>
  )
}
